import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Actions,
  ofActionCanceled,
  ofActionErrored,
  ofActionSuccessful,
  Store,
} from '@ngxs/store';
import { defer, forkJoin, merge, of, race, Subject, zip } from 'rxjs';
import { catchError, mapTo, take, timeout, tap } from 'rxjs/operators';
import { QuoteActions, QuoteState } from '@nibtravel/shared/data-access/quote';
import { PlansAction } from '@nibtravel/shared/data-access/plans';
import { AppConfigState } from '@nibtravel/shared/util/app-config';
import { Route, routeFromRoot } from '@nibtravel/shared/util/constants';
import {
  parseEmailParams,
  parseQuoteParams,
} from './helpers/quote-param.helper';
import { DomService } from '@nibtravel/shared/util/dom';

@Component({
  selector: 'nibtravel-quote-loader',
  templateUrl: './quote-loader.component.html',
})
export class QuoteLoaderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject();

  constructor(
    private store: Store,
    private actions: Actions,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private domService: DomService
  ) {}

  ngOnInit(): void {
    if (this.activatedRoute.snapshot.paramMap.has('id')) {
      this.handleEmailParams();
    } else if (this.activatedRoute.snapshot.queryParamMap.keys.length > 0) {
      this.handleQuoteParams();
    } else {
      this.navigateHome();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private navigateHome(): void {
    this.router.navigate([Route.Root]);
  }

  private handleQuoteParams(): void {
    const quote = parseQuoteParams(this.activatedRoute.snapshot.queryParams);
    const utmParams = this.domService.getUtmParams();

    // This needs be deferred until the success and errors have subscribed
    const dispatchQuote$ = defer(() => {
      const actions: any[] = [new QuoteActions.SetQuoteParams(quote)];
      if (utmParams) {
        actions.push(new QuoteActions.SetAnalyticParams(utmParams));
      }

      return this.store.dispatch(actions);
    });

    // Success
    const quoteSuccess$ = this.actions.pipe(
      ofActionSuccessful(QuoteActions.GetQuote),
      take(1),
      mapTo(true)
    );
    const loadPlansSuccess$ = this.actions.pipe(
      ofActionSuccessful(PlansAction.LoadPlansData),
      take(1),
      mapTo(true)
    );
    const success$ = forkJoin([quoteSuccess$, loadPlansSuccess$]).pipe(
      mapTo(true)
    );

    // Error or Cancelled
    const errorActions$ = this.actions.pipe(
      ofActionErrored(QuoteActions.GetQuote, PlansAction.LoadPlansData)
    );
    const cancelActions$ = this.actions.pipe(
      ofActionCanceled(QuoteActions.GetQuote, PlansAction.LoadPlansData)
    );
    const errors$ = merge(errorActions$, cancelActions$).pipe(
      timeout(10000), // Wait for 10sec otherwise error out
      mapTo(false)
    );

    zip(race(success$, errors$), dispatchQuote$)
      .pipe(take(1))
      .subscribe({
        next: ([success]) => {
          if (success) {
            this.router.navigate(routeFromRoot(Route.Quote));
          } else {
            this.navigateHome();
          }
        },
        error: () => {
          this.navigateHome();
        },
      });
  }

  private handleEmailParams(): void {
    const brandCode = this.store.selectSnapshot(AppConfigState.getBrand);
    const params = parseEmailParams(this.activatedRoute.snapshot.queryParams);
    const quoteId = this.activatedRoute.snapshot.paramMap.get('id') as string;
    const planId = this.activatedRoute.snapshot.paramMap.has('planId')
      ? Number(this.activatedRoute.snapshot.paramMap.get('planId'))
      : undefined;

    const utmParams = this.domService.getUtmParams();

    const actionsToDispatch = [
      new QuoteActions.QqcReset(),
      new QuoteActions.SetPartnerCode(params.partnerCode),
      new QuoteActions.FindQuote(brandCode, quoteId, planId),
      new PlansAction.LoadOptionsSummary(planId as number, brandCode),
    ];

    if (utmParams) {
      actionsToDispatch.push(new QuoteActions.SetAnalyticParams(utmParams));
    }

    const dispatchQuote$ = defer(() => {
      return this.store.dispatch(actionsToDispatch);
    });

    // Success
    const quoteSuccess$ = this.actions.pipe(
      ofActionSuccessful(QuoteActions.FindQuote),
      take(1),
      mapTo(true)
    );
    const loadPlansSuccess$ = this.actions.pipe(
      ofActionSuccessful(PlansAction.LoadPlansData),
      take(1),
      mapTo(true)
    );

    const LoadOptionsSummarySuccess$ = this.actions.pipe(
      ofActionSuccessful(PlansAction.LoadOptionsSummary),
      take(1),
      mapTo(true)
    );
    const success$ = forkJoin([
      quoteSuccess$,
      loadPlansSuccess$,
      LoadOptionsSummarySuccess$,
    ]).pipe(mapTo(true));

    // Error or Cancelled
    const errorActions$ = this.actions.pipe(
      ofActionErrored(QuoteActions.FindQuote, PlansAction.LoadPlansData)
    );
    const cancelActions$ = this.actions.pipe(
      ofActionCanceled(QuoteActions.FindQuote, PlansAction.LoadPlansData)
    );
    const errors$ = merge(errorActions$, cancelActions$).pipe(
      timeout(10000), // Wait for 10sec otherwise error out
      mapTo(false)
    );

    zip(race(success$, errors$), dispatchQuote$)
      .pipe(
        take(1),
        catchError(() => {
          return of([false]);
        }),
        tap(() => {
          if (planId) {
            const policyHolders = this.store.selectSnapshot(
              QuoteState.getPolicyHolders
            );
            this.store.dispatch(
              new PlansAction.CalculateImplicitCancellationValue(
                planId,
                policyHolders
              )
            );
          }
        })
      )
      .subscribe({
        next: ([success]) => {
          if (success) {
            if (planId) {
              this.router.navigate(routeFromRoot(Route.Quote, Route.Options));
            } else {
              this.router.navigate(routeFromRoot(Route.Quote));
            }
          } else {
            this.navigateHome();
          }
        },
        error: () => this.navigateHome(),
      });
  }
}
